/**
 * Cockpit Phase 6 — the 5 archetype CockpitSpec scaffolds (COCKPIT-PLAN §4).
 *
 * Five canonical templates covering the read / read+live / read+act /
 * read+act+state matrix. Authoring "edits a valid archetype spec via typed
 * SpecMutations (deltas), never whole-doc regeneration" — these are the
 * known-good starting points AND the GOLDEN SET the publish gate G1 lints
 * against (they MUST pass every hard gate; if one fails, the gate or the
 * archetype is wrong).
 *
 *   1. Asset Health Monitor       — read + live (metrics + status grid + chart)
 *   2. Work Queue / Ops Board      — read + act (table with a per-row action)
 *   3. KPI Summary / Exec Rollup   — read (metric cards + chart)
 *   4. Incident / Alarm Timeline   — read + live (timeline + alert banner)
 *   5. Parameter Control Panel     — read + act + state (metrics + action buttons)
 *
 * Each is a valid CockpitSpec (parses against CockpitSpecSchema). The binding +
 * action refs are declared in declaredBindingIds / declaredActionIds so the
 * ref-validation passes; a real cockpit's manifest fills these bindings in.
 */

import type { CockpitSpec } from './cockpit-spec.js';
import { COCKPIT_SPEC_VERSION } from './cockpit-spec.js';

/** The archetype slugs. The original 5 (read / read+live / read+act / +state)
 * plus 2 feat/cockpit additions that exercise the comprehensive palette end to
 * end (charts + a geospatial map + icons). */
export const COCKPIT_ARCHETYPE_SLUGS = [
  'asset_health_monitor',
  'work_queue_ops_board',
  'kpi_exec_rollup',
  'incident_alarm_timeline',
  'parameter_control_panel',
  'site_geo_overview',
  'equipment_diagnostics',
] as const;
export type CockpitArchetypeSlug = (typeof COCKPIT_ARCHETYPE_SLUGS)[number];

/* ── 1. Asset Health Monitor (read + live) ──────────────────────────────── */
const ASSET_HEALTH_MONITOR: CockpitSpec = {
  specVersion: COCKPIT_SPEC_VERSION,
  declaredBindingIds: ['asset_metrics', 'asset_status', 'vibration_trend', 'health_live'],
  declaredActionIds: [],
  meta: { title: 'Asset Health Monitor', archetype: 'asset_health_monitor', authoredBy: 'archetype' },
  root: {
    id: 'root',
    component: 'CockpitContainer',
    props: { title: 'Asset Health Monitor', subtitle: 'Live equipment health at a glance' },
    children: [
      {
        id: 'live_badge',
        component: 'LiveIndicator',
        props: { label: 'Live', subscribeBinding: 'health_live' },
      },
      {
        id: 'kpi_vibration',
        component: 'MetricCard',
        props: { label: 'Vibration', unit: 'mm/s', tone: 'warning', valueBinding: 'asset_metrics', valuePath: 'vibration' },
      },
      {
        id: 'kpi_temp',
        component: 'MetricCard',
        props: { label: 'Bearing temp', unit: '°C', tone: 'normal', valueBinding: 'asset_metrics', valuePath: 'bearingTemp' },
      },
      {
        id: 'status_grid',
        component: 'StatusCardGrid',
        props: { title: 'Asset health', itemsBinding: 'asset_status', titleField: 'asset', valueField: 'state', toneField: 'tone', columns: 3 },
      },
      {
        id: 'trend_chart',
        component: 'ChartCard',
        props: { title: 'Vibration trend (24h)', seriesBinding: 'vibration_trend', chartType: 'line', xField: 'ts', yField: 'value', unit: 'mm/s', tone: 'info' },
      },
    ],
  },
};

/* ── 2. Work Queue / Ops Board (read + act) ─────────────────────────────── */
const WORK_QUEUE_OPS_BOARD: CockpitSpec = {
  specVersion: COCKPIT_SPEC_VERSION,
  declaredBindingIds: ['open_work_orders', 'queue_counts'],
  declaredActionIds: ['reassign_wo'],
  meta: { title: 'Work Queue', archetype: 'work_queue_ops_board', authoredBy: 'archetype' },
  root: {
    id: 'root',
    component: 'CockpitContainer',
    props: { title: 'Work Queue', subtitle: 'Open work orders and assignments' },
    children: [
      {
        id: 'queue_summary',
        component: 'StatusCardGrid',
        props: { title: 'Queue', itemsBinding: 'queue_counts', titleField: 'bucket', valueField: 'count', toneField: 'tone', columns: 4 },
      },
      {
        id: 'wo_search',
        component: 'SearchBar',
        props: { placeholder: 'Search work orders', targetId: 'wo_table' },
      },
      {
        id: 'wo_filters',
        component: 'FilterChips',
        props: {
          targetId: 'wo_table',
          field: 'status',
          chips: [
            { label: 'Open', value: 'open' },
            { label: 'In progress', value: 'in_progress' },
            { label: 'Blocked', value: 'blocked' },
          ],
        },
      },
      {
        id: 'wo_table',
        component: 'DataTable',
        props: {
          title: 'Open work orders',
          rowsBinding: 'open_work_orders',
          rowKeyField: 'id',
          columns: [
            { key: 'id', label: 'WO #', align: 'left' },
            { key: 'asset', label: 'Asset', align: 'left' },
            { key: 'priority', label: 'Priority', align: 'left' },
            { key: 'status', label: 'Status', align: 'left' },
          ],
          rowAction: 'reassign_wo',
          rowActionLabel: 'Reassign',
          emptyMessage: 'No open work orders.',
          pageSize: 50,
        },
      },
    ],
  },
};

/* ── 3. KPI Summary / Exec Rollup (read) ────────────────────────────────── */
const KPI_EXEC_ROLLUP: CockpitSpec = {
  specVersion: COCKPIT_SPEC_VERSION,
  declaredBindingIds: ['kpi_uptime', 'kpi_mttr', 'kpi_backlog', 'throughput_trend'],
  declaredActionIds: [],
  meta: { title: 'Operations Rollup', archetype: 'kpi_exec_rollup', authoredBy: 'archetype' },
  root: {
    id: 'root',
    component: 'CockpitContainer',
    props: { title: 'Operations Rollup', subtitle: 'Plant performance this shift' },
    children: [
      {
        id: 'kpi_uptime',
        component: 'MetricCard',
        props: { label: 'Uptime', unit: '%', tone: 'success', valueBinding: 'kpi_uptime', valuePath: 'value', delta: { value: '+0.3', direction: 'up', tone: 'success' } },
      },
      {
        id: 'kpi_mttr',
        component: 'MetricCard',
        props: { label: 'MTTR', unit: 'h', tone: 'normal', valueBinding: 'kpi_mttr', valuePath: 'value' },
      },
      {
        id: 'kpi_backlog',
        component: 'MetricCard',
        props: { label: 'Backlog', unit: 'WOs', tone: 'warning', valueBinding: 'kpi_backlog', valuePath: 'value', delta: { value: '+4', direction: 'up', tone: 'warning' } },
      },
      {
        id: 'throughput',
        component: 'ChartCard',
        props: { title: 'Throughput (7d)', seriesBinding: 'throughput_trend', chartType: 'bar', xField: 'day', yField: 'completed', tone: 'info' },
      },
    ],
  },
};

/* ── 4. Incident / Alarm Timeline (read + live) ─────────────────────────── */
const INCIDENT_ALARM_TIMELINE: CockpitSpec = {
  specVersion: COCKPIT_SPEC_VERSION,
  declaredBindingIds: ['active_alarms', 'alarm_history', 'alarm_live'],
  declaredActionIds: [],
  meta: { title: 'Alarm Timeline', archetype: 'incident_alarm_timeline', authoredBy: 'archetype' },
  root: {
    id: 'root',
    component: 'CockpitContainer',
    props: { title: 'Alarm Timeline', subtitle: 'Active alarms and recent history' },
    children: [
      {
        id: 'active_banner',
        component: 'AlertBanner',
        props: { tone: 'critical', title: 'Active alarms', message: 'Review and acknowledge the alarms below.' },
      },
      {
        id: 'alarm_feed',
        component: 'NotificationFeed',
        props: { title: 'Incoming alarms', feedBinding: 'active_alarms', subscribeBinding: 'alarm_live', titleField: 'message', timestampField: 'raisedAt', maxItems: 50 },
      },
      {
        id: 'history_timeline',
        component: 'Timeline',
        props: { title: 'Alarm history (24h)', eventsBinding: 'alarm_history', timestampField: 'ts', titleField: 'message', toneField: 'tone' },
      },
    ],
  },
};

/* ── 5. Parameter Control Panel (read + act + state) ────────────────────── */
const PARAMETER_CONTROL_PANEL: CockpitSpec = {
  specVersion: COCKPIT_SPEC_VERSION,
  declaredBindingIds: ['setpoints', 'live_readings', 'operator_notes'],
  declaredActionIds: ['apply_setpoint', 'reset_setpoint'],
  meta: { title: 'Parameter Control Panel', archetype: 'parameter_control_panel', authoredBy: 'archetype' },
  root: {
    id: 'root',
    component: 'CockpitContainer',
    props: { title: 'Parameter Control Panel', subtitle: 'Monitor and adjust setpoints (gated)' },
    children: [
      {
        id: 'reading_flow',
        component: 'MetricCard',
        props: { label: 'Flow rate', unit: 'm³/h', tone: 'normal', valueBinding: 'live_readings', valuePath: 'flow' },
      },
      {
        id: 'reading_pressure',
        component: 'MetricCard',
        props: { label: 'Pressure', unit: 'bar', tone: 'warning', valueBinding: 'live_readings', valuePath: 'pressure' },
      },
      {
        id: 'setpoint_table',
        component: 'DataTable',
        props: {
          title: 'Setpoints',
          rowsBinding: 'setpoints',
          rowKeyField: 'param',
          columns: [
            { key: 'param', label: 'Parameter', align: 'left' },
            { key: 'current', label: 'Current', align: 'right' },
            { key: 'target', label: 'Target', align: 'right' },
          ],
          emptyMessage: 'No setpoints configured.',
        },
      },
      {
        id: 'apply_btn',
        component: 'ActionButton',
        props: { label: 'Apply setpoint', action: 'apply_setpoint', tone: 'primary', confirm: true },
      },
      {
        id: 'reset_btn',
        component: 'ActionButton',
        props: { label: 'Reset to default', action: 'reset_setpoint', tone: 'secondary', confirm: true },
      },
      {
        id: 'op_notes',
        component: 'RichTextEditor',
        props: { label: 'Operator note', stateBinding: 'operator_notes', placeholder: 'Record any manual adjustment…', maxLength: 4000 },
      },
    ],
  },
};

/* ── 6. Site Geo Overview (read + live: MAP + chart + icons) ────────────── */
// Exercises the comprehensive palette: MapView (air-gap-safe inline-SVG geo),
// a donut Chart, status Stats with icons, a Badge, a Divider, a TreeList.
const SITE_GEO_OVERVIEW: CockpitSpec = {
  specVersion: COCKPIT_SPEC_VERSION,
  declaredBindingIds: ['site_locations', 'sites_by_status', 'site_tree', 'site_count', 'site_live'],
  declaredActionIds: [],
  meta: { title: 'Site Overview', archetype: 'site_geo_overview', authoredBy: 'archetype' },
  root: {
    id: 'root',
    component: 'CockpitContainer',
    props: { title: 'Site Overview', subtitle: 'Fleet status across all sites' },
    children: [
      {
        id: 'live_badge',
        component: 'LiveIndicator',
        props: { label: 'Live', subscribeBinding: 'site_live' },
      },
      {
        id: 'sites_stat',
        component: 'Stat',
        props: { label: 'Active sites', icon: 'map-pin', tone: 'info', valueBinding: 'site_count', valuePath: 'total' },
      },
      {
        id: 'alerts_badge',
        component: 'Badge',
        props: { label: '3 alerts', tone: 'critical', icon: 'alert-triangle', variant: 'soft' },
      },
      {
        id: 'site_map',
        component: 'MapView',
        props: {
          title: 'Site map',
          pointsBinding: 'site_locations',
          latKey: 'lat',
          lngKey: 'lng',
          labelKey: 'name',
          statusKey: 'status',
          points: [
            { lat: 29.76, lng: -95.37, name: 'Houston plant', status: 'success' },
            { lat: 32.78, lng: -96.8, name: 'Dallas depot', status: 'warning' },
            { lat: 30.27, lng: -97.74, name: 'Austin hub', status: 'critical' },
          ],
        },
      },
      {
        id: 'status_divider',
        component: 'Divider',
        props: { label: 'Breakdown' },
      },
      {
        id: 'status_donut',
        component: 'Chart',
        props: {
          title: 'Sites by status',
          chartType: 'donut',
          dataBinding: 'sites_by_status',
          xKey: 'status',
          yKeys: ['count'],
          colors: ['success', 'warning', 'critical'],
          legend: true,
          data: [
            { status: 'Healthy', count: 8 },
            { status: 'Degraded', count: 2 },
            { status: 'Down', count: 1 },
          ],
        },
      },
      {
        id: 'site_hierarchy',
        component: 'TreeList',
        props: {
          title: 'Site hierarchy',
          nodesBinding: 'site_tree',
          labelField: 'name',
          childrenField: 'children',
          toneField: 'status',
          nodes: [
            {
              name: 'Texas region',
              children: [
                { name: 'Houston plant', status: 'success' },
                { name: 'Dallas depot', status: 'warning' },
              ],
            },
          ],
        },
      },
    ],
  },
};

/* ── 7. Equipment Diagnostics (read + act: charts + gauge + controls) ───── */
// Exercises Chart (line + gauge), ProgressBar, ToggleGroup, ButtonGroup, Icon.
const EQUIPMENT_DIAGNOSTICS: CockpitSpec = {
  specVersion: COCKPIT_SPEC_VERSION,
  declaredBindingIds: ['vibration_series', 'oil_level', 'health_score', 'temp_series'],
  declaredActionIds: ['ack_alarm', 'create_wo'],
  meta: { title: 'Equipment Diagnostics', archetype: 'equipment_diagnostics', authoredBy: 'archetype' },
  root: {
    id: 'root',
    component: 'CockpitContainer',
    props: { title: 'Equipment Diagnostics', subtitle: 'Pump P-401 deep dive' },
    children: [
      {
        id: 'health_icon',
        component: 'Icon',
        props: { name: 'gauge', tone: 'warning', size: 'lg', label: 'Health gauge' },
      },
      {
        id: 'range_toggle',
        component: 'ToggleGroup',
        props: {
          label: 'Range',
          targetId: 'vib_chart',
          field: 'range',
          options: [
            { label: '1h', value: '1h' },
            { label: '24h', value: '24h' },
            { label: '7d', value: '7d' },
          ],
          defaultValue: '24h',
        },
      },
      {
        id: 'vib_chart',
        component: 'Chart',
        props: {
          title: 'Vibration trend',
          chartType: 'line',
          dataBinding: 'vibration_series',
          xKey: 'ts',
          yKeys: ['value'],
          unit: 'mm/s',
          colors: ['warning'],
          data: [
            { ts: '00:00', value: 2.1 },
            { ts: '08:00', value: 2.9 },
            { ts: '16:00', value: 3.6 },
          ],
        },
      },
      {
        id: 'temp_chart',
        component: 'Chart',
        props: {
          title: 'Bearing temp',
          chartType: 'area',
          dataBinding: 'temp_series',
          xKey: 'ts',
          yKeys: ['c'],
          unit: '°C',
          colors: ['info'],
          data: [
            { ts: '00:00', c: 42 },
            { ts: '08:00', c: 47 },
            { ts: '16:00', c: 51 },
          ],
        },
      },
      {
        id: 'health_gauge',
        component: 'Chart',
        props: {
          title: 'Health score',
          chartType: 'gauge',
          dataBinding: 'health_score',
          xKey: 'label',
          yKeys: ['score'],
          unit: '%',
          max: 100,
          colors: ['warning'],
          data: [{ label: 'Health', score: 68 }],
        },
      },
      {
        id: 'oil_progress',
        component: 'ProgressBar',
        props: { label: 'Oil level', value: 34, max: 100, tone: 'warning', valueBinding: 'oil_level', valuePath: 'pct' },
      },
      {
        id: 'diag_divider',
        component: 'Divider',
        props: { label: 'Actions' },
      },
      {
        id: 'diag_actions',
        component: 'ButtonGroup',
        props: {
          buttons: [
            { label: 'Acknowledge', action: 'ack_alarm', tone: 'primary', icon: 'check' },
            { label: 'Create WO', action: 'create_wo', tone: 'secondary', icon: 'wrench' },
          ],
        },
      },
    ],
  },
};

/** The archetypes keyed by slug. Frozen — these are the golden set. */
export const COCKPIT_ARCHETYPES: Readonly<Record<CockpitArchetypeSlug, CockpitSpec>> =
  Object.freeze({
    asset_health_monitor: ASSET_HEALTH_MONITOR,
    work_queue_ops_board: WORK_QUEUE_OPS_BOARD,
    kpi_exec_rollup: KPI_EXEC_ROLLUP,
    incident_alarm_timeline: INCIDENT_ALARM_TIMELINE,
    parameter_control_panel: PARAMETER_CONTROL_PANEL,
    site_geo_overview: SITE_GEO_OVERVIEW,
    equipment_diagnostics: EQUIPMENT_DIAGNOSTICS,
  });

/** Get an archetype scaffold (a deep clone, so callers can mutate freely). */
export function getCockpitArchetype(slug: CockpitArchetypeSlug): CockpitSpec {
  return structuredClone(COCKPIT_ARCHETYPES[slug]);
}
